import type { CurrentUserProfileResponse } from "@/types/user";

// Role/permission checks are NOT done by database id — ids differ per
// deployment (the `admin = id 1` assumption was a latent bug). Identity checks
// live server-side; the client reads served decisions. The removed id-based
// helpers (hasRole/hasAnyRole/hasAllRoles/hasPermission) were unused dead code.

/** Where anyone without a configured landing page goes. */
export const FALLBACK_ROUTE = "/appointments";

/**
 * Where a user lands after logging in, when they did not ask for a specific
 * page. Reads roles.default_route, taking the first role that carries one — so
 * landing pages are configurable per deployment rather than compiled in.
 */
export function getDefaultRouteForUser(
  user: CurrentUserProfileResponse | null | undefined
): string {
  if (!user || !user.roles || user.roles.length === 0) {
    // A user with no roles is an offboarded account or a service account. It
    // has no business landing on user administration, which it cannot open.
    return FALLBACK_ROUTE;
  }

  // Find the first role with a non-empty default_route
  for (const role of user.roles) {
    if (role.default_route && role.default_route.trim() !== "") {
      const route = role.default_route.trim();
      try {
        // Strip host if an absolute URL was stored — keep only the path
        return new URL(route).pathname;
      } catch {
        return route;
      }
    }
  }

  // No role carries a route. Appointments is the one page essentially every
  // staff role can open and act on; the previous fallback of /settings/users
  // sent doctors, radiologists and branch managers to a page they are not
  // permitted to see, which renders as an empty shell rather than an error.
  return FALLBACK_ROUTE;
}
